import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { 
  ArrowLeft, Send, Loader2, Clock, AlertTriangle, CheckCircle2, 
  User, Building2, Phone, Mail, HandMetal, Timer, Sparkles, Wand2, Copy
} from 'lucide-react';

export default function TicketDetail() {
  const { API, user } = useAuth();
  const navigate = useNavigate();
  const { ticket_id } = useParams();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [sending, setSending] = useState(false);
  const [escalationContacts, setEscalationContacts] = useState([]);
  const commentsEndRef = useRef(null);

  // AI Assist state
  const [aiSummary, setAiSummary] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiTone, setAiTone] = useState('professional');
  const [aiReply, setAiReply] = useState(null);
  const [aiReplyLoading, setAiReplyLoading] = useState(false);

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchTicket();
    fetchEscalationContacts();
  }, [ticket_id]);

  const fetchTicket = async () => {
    try {
      const res = await fetch(`${API}/tickets/${ticket_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.status === 404) {
        toast.error('Ticket not found');
        navigate('/tickets');
        return;
      }

      const data = await res.json();
      setTicket(data);
    } catch (error) {
      toast.error('Failed to load ticket');
    } finally {
      setLoading(false);
    }
  };

  const fetchEscalationContacts = async () => {
    try {
      const res = await fetch(`${API}/tickets/escalation-contacts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setEscalationContacts(data.contacts || []);
    } catch (error) {
      console.error('Failed to fetch escalation contacts');
    }
  };

  const handleAssignToSelf = async () => {
    try {
      const res = await fetch(`${API}/tickets/${ticket_id}/assign-self`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Ticket assigned to you');
        fetchTicket();
      } else {
        toast.error('Failed to assign ticket');
      }
    } catch (error) {
      toast.error('Network error');
    }
  };

  const handleUpdateStatus = async (status) => {
    try {
      const res = await fetch(`${API}/tickets/${ticket_id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status })
      });

      if (res.ok) {
        toast.success(`Status updated to ${status}`);
        fetchTicket();
      } else {
        toast.error('Failed to update status');
      }
    } catch (error) {
      toast.error('Network error');
    }
  };

  const handleEscalate = async (escalationId) => {
    try {
      const res = await fetch(`${API}/tickets/${ticket_id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          escalated_to: escalationId,
          escalation_reason: 'Manual escalation by staff'
        })
      });

      if (res.ok) {
        toast.success('Ticket escalated');
        fetchTicket();
      } else {
        toast.error('Failed to escalate');
      }
    } catch (error) {
      toast.error('Network error');
    }
  };

  const handleSendComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;

    setSending(true);
    try {
      const res = await fetch(`${API}/tickets/${ticket_id}/comments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          content: comment,
          is_internal: isInternal
        })
      });

      if (res.ok) {
        setComment('');
        setIsInternal(false);
        await fetchTicket();
        toast.success(isInternal ? 'Internal note added' : 'Reply sent');
      } else {
        toast.error('Failed to send');
      }
    } catch (error) {
      toast.error('Network error');
    } finally {
      setSending(false);
    }
  };

  const handleAiSummarise = async () => {
    setAiLoading(true);
    try {
      const res = await fetch(`${API}/ai/tickets/${ticket_id}/summarise`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      setAiSummary(data);
      toast.success('Summary ready');
    } catch (e) {
      toast.error(e.message || 'AI summary failed');
    } finally {
      setAiLoading(false);
    }
  };

  const handleAiSuggestReply = async () => {
    setAiReplyLoading(true);
    setAiReply(null);
    try {
      const res = await fetch(`${API}/ai/tickets/${ticket_id}/suggest-reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ tone: aiTone }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      setAiReply(data);
    } catch (e) {
      toast.error(e.message || 'AI reply failed');
    } finally {
      setAiReplyLoading(false);
    }
  };

  const useSuggestedReply = () => {
    if (!aiReply?.reply) return;
    setComment(aiReply.reply);
    setIsInternal(false);
    toast.success('Draft loaded into reply — edit and send');
  };


  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-blue-100 text-blue-700',
      in_progress: 'bg-amber-100 text-amber-700',
      pending_customer: 'bg-purple-100 text-purple-700',
      resolved: 'bg-emerald-100 text-emerald-700',
      closed: 'bg-slate-100 text-slate-700'
    };
    return <Badge className={styles[status] || 'bg-slate-100'}>{status?.replace('_', ' ')}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      low: 'bg-slate-100 text-slate-600',
      medium: 'bg-blue-100 text-blue-600',
      high: 'bg-orange-100 text-orange-600',
      critical: 'bg-red-100 text-red-600'
    };
    return <Badge className={styles[priority] || 'bg-slate-100'}>{priority}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!ticket) return null;

  const isResolved = ['resolved', 'closed'].includes(ticket.status);
  const isAssignedToMe = ticket.assigned_to === user?.email;
  const slaStatus = ticket.sla_status || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/tickets')} className="mb-2">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Tickets
          </Button>
          <div className="flex items-center gap-3">
            <span className="font-mono text-sm text-slate-400">{ticket.ticket_number}</span>
            {getStatusBadge(ticket.status)}
            {getPriorityBadge(ticket.priority)}
          </div>
          <h1 className="text-xl font-bold text-slate-900 mt-1">{ticket.subject}</h1>
        </div>
        
        {/* Actions */}
        <div className="flex items-center gap-2">
          {!isAssignedToMe && !isResolved && (
            <Button onClick={handleAssignToSelf} variant="outline">
              <HandMetal className="mr-2 h-4 w-4" /> Take Ticket
            </Button>
          )}
          {isAssignedToMe && !isResolved && (
            <Select onValueChange={handleUpdateStatus}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Update Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="pending_customer">Pending Customer</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Issue Description</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="whitespace-pre-wrap text-slate-700">{ticket.description}</p>
            </CardContent>
          </Card>

          {/* AI Assist — powered by Claude */}
          <Card className="border-indigo-100 bg-gradient-to-br from-indigo-50/40 to-fuchsia-50/30" data-testid="ai-assist-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-indigo-600" />
                AI Assist
                <Badge className="ml-1 bg-indigo-100 text-indigo-700 text-[10px] border-0">Claude</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={handleAiSummarise}
                  disabled={aiLoading} data-testid="ai-summarise-btn">
                  {aiLoading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                  Summarise thread
                </Button>
                <div className="inline-flex items-center gap-1 border rounded-md px-2 h-9 bg-white">
                  <span className="text-xs text-slate-500">Tone:</span>
                  <select value={aiTone} onChange={e => setAiTone(e.target.value)}
                    className="text-xs bg-transparent focus:outline-none" data-testid="ai-tone-select">
                    <option value="professional">Professional</option>
                    <option value="empathetic">Empathetic</option>
                    <option value="brief">Brief</option>
                  </select>
                </div>
                <Button size="sm" onClick={handleAiSuggestReply} disabled={aiReplyLoading}
                  className="bg-indigo-600 hover:bg-indigo-700" data-testid="ai-suggest-reply-btn">
                  {aiReplyLoading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Wand2 className="w-3.5 h-3.5 mr-1" />}
                  Suggest reply
                </Button>
              </div>

              {aiSummary && (
                <div className="bg-white border rounded-md p-3 text-sm space-y-2" data-testid="ai-summary-panel">
                  <p className="text-slate-700">{aiSummary.summary}</p>
                  {Array.isArray(aiSummary.key_facts) && aiSummary.key_facts.length > 0 && (
                    <ul className="list-disc pl-5 text-xs text-slate-600 space-y-0.5">
                      {aiSummary.key_facts.map((f, i) => <li key={i}>{f}</li>)}
                    </ul>
                  )}
                  <div className="flex items-center gap-3 text-xs text-slate-500 pt-1 border-t">
                    <span>Sentiment: <span className="font-medium text-slate-700">{aiSummary.customer_sentiment}</span></span>
                    {aiSummary.suggested_next_step && <span>· Next: {aiSummary.suggested_next_step}</span>}
                  </div>
                </div>
              )}

              {aiReply && (
                <div className="bg-white border rounded-md p-3 text-sm space-y-2" data-testid="ai-reply-panel">
                  <p className="whitespace-pre-wrap text-slate-700">{aiReply.reply}</p>
                  {aiReply.notes && <p className="text-xs text-amber-700 border-t pt-2">Note: {aiReply.notes}</p>}
                  <div className="flex items-center justify-between pt-1 border-t">
                    <span className="text-[11px] text-slate-500">Confidence: {(aiReply.confidence * 100).toFixed(0)}%</span>
                    <Button size="sm" variant="ghost" onClick={useSuggestedReply} data-testid="ai-use-reply-btn">
                      <Copy className="w-3.5 h-3.5 mr-1" /> Use this draft
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Conversation */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Conversation</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {ticket.comments?.length === 0 ? (
                <div className="p-6 text-center text-slate-500 text-sm">
                  No messages yet
                </div>
              ) : (
                <div className="divide-y max-h-[500px] overflow-y-auto">
                  {ticket.comments?.map((c) => (
                    <div 
                      key={c.comment_id} 
                      className={`p-4 ${c.is_internal ? 'bg-amber-50 border-l-4 border-amber-300' : c.author_type === 'customer' ? 'bg-emerald-50' : ''}`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                          c.author_type === 'customer' ? 'bg-emerald-200 text-emerald-700' : 'bg-blue-200 text-blue-700'
                        }`}>
                          {c.author_name?.charAt(0) || 'U'}
                        </div>
                        <div>
                          <span className="font-medium text-sm">{c.author_name}</span>
                          {c.is_internal && <Badge className="ml-2 bg-amber-200 text-amber-800">Internal</Badge>}
                        </div>
                        <span className="text-xs text-slate-400 ml-auto">
                          {new Date(c.created_at).toLocaleString('en-IN', {
                            day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
                          })}
                        </span>
                      </div>
                      <p className="text-sm text-slate-700 ml-10 whitespace-pre-wrap">{c.content}</p>
                    </div>
                  ))}
                  <div ref={commentsEndRef} />
                </div>
              )}

              {/* Comment Form */}
              {!isResolved && (
                <form onSubmit={handleSendComment} className="p-4 border-t bg-slate-50">
                  <Textarea
                    placeholder={isInternal ? "Add internal note (not visible to customer)..." : "Reply to customer..."}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    rows={3}
                    className={isInternal ? 'border-amber-300 bg-amber-50' : ''}
                  />
                  <div className="flex items-center justify-between mt-3">
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="internal" 
                        checked={isInternal}
                        onCheckedChange={setIsInternal}
                      />
                      <Label htmlFor="internal" className="text-sm text-slate-600">Internal note</Label>
                    </div>
                    <Button type="submit" disabled={sending || !comment.trim()}>
                      {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                      {isInternal ? 'Add Note' : 'Send Reply'}
                    </Button>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* SLA Status */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Timer className="h-4 w-4" /> SLA Status
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex justify-between items-center">
                <span className="text-slate-500">First Response</span>
                <span className={`font-medium ${
                  slaStatus.first_response === 'met' ? 'text-emerald-600' :
                  slaStatus.first_response === 'breached' ? 'text-red-600' : 'text-amber-600'
                }`}>
                  {slaStatus.first_response === 'met' ? '✓ Met' :
                   slaStatus.first_response === 'breached' ? '✗ Breached' :
                   slaStatus.first_response || 'Pending'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-500">Resolution</span>
                <span className={`font-medium ${
                  slaStatus.resolution === 'met' ? 'text-emerald-600' :
                  slaStatus.resolution === 'breached' ? 'text-red-600' : 'text-amber-600'
                }`}>
                  {slaStatus.resolution === 'met' ? '✓ Met' :
                   slaStatus.resolution === 'breached' ? '✗ Breached' :
                   slaStatus.resolution || 'Pending'}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Customer Info */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Building2 className="h-4 w-4" /> Customer
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <p className="font-medium">{ticket.customer_name}</p>
              {ticket.circuit_code && (
                <p className="text-slate-500 font-mono">{ticket.circuit_code}</p>
              )}
              {ticket.portal_user_phone && (
                <p className="flex items-center gap-2 text-slate-500">
                  <Phone className="h-3 w-3" /> {ticket.portal_user_phone}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Assignment */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <User className="h-4 w-4" /> Assignment
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              {ticket.assigned_to_name ? (
                <div>
                  <p className="font-medium">{ticket.assigned_to_name}</p>
                  <p className="text-xs text-slate-500">
                    Assigned {new Date(ticket.assigned_at).toLocaleDateString('en-IN')}
                  </p>
                </div>
              ) : (
                <p className="text-slate-500">Unassigned</p>
              )}
            </CardContent>
          </Card>

          {/* Escalation */}
          {ticket.escalated_to && (
            <Card className="border-red-200 bg-red-50">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2 text-red-700">
                  <AlertTriangle className="h-4 w-4" /> Escalated
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm">
                <p className="font-medium text-red-700">{ticket.escalated_to_name}</p>
                <p className="text-xs text-red-600">Level {ticket.escalation_level}</p>
              </CardContent>
            </Card>
          )}

          {/* Escalate Button */}
          {!isResolved && escalationContacts.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Escalate</CardTitle>
              </CardHeader>
              <CardContent>
                <Select onValueChange={handleEscalate}>
                  <SelectTrigger>
                    <SelectValue placeholder="Escalate to..." />
                  </SelectTrigger>
                  <SelectContent>
                    {escalationContacts.map((c) => (
                      <SelectItem key={c.escalation_id} value={c.escalation_id}>
                        {c.name} (L{c.level})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>
          )}

          {/* Ticket Details */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Category</span>
                <span>{ticket.category}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Sub-category</span>
                <span>{ticket.sub_category?.replace('_', ' ')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Language</span>
                <span className="uppercase">{ticket.language}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Created</span>
                <span>{new Date(ticket.created_at).toLocaleDateString('en-IN')}</span>
              </div>
              {ticket.resolved_at && (
                <div className="flex justify-between">
                  <span className="text-slate-500">Resolved</span>
                  <span className="text-emerald-600">
                    {new Date(ticket.resolved_at).toLocaleDateString('en-IN')}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
